import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  FEATURE_CATEGORIES,
  SUPPORTED_TENANT_FEATURE_KEYS,
} from '@/lib/tenant-features'
import {
  applyTenantCustomDomain,
  deprovisionTenant,
  suspendTenant,
  activateTenant,
  getReleasesCatalog,
  getTenantById,
  getTenantReleases,
  getProvisionJobs,
  getInvoices,
  getTenantStage,
  provisionTenant,
  requestTenantSuperadminAccess,
  rollbackTenant,
  deleteTenantStage,
  updateTenant,
  upgradeTenant,
  upsertTenantStage,
} from '@/services'
import { AppButton, EmptyState, StatusBadge, HealthDot, Modal, Skeleton, TableSkeleton, Tabs, rowActivation } from '@/components/platform/primitives'
import { Icon } from '@/components/platform/Icon'
import { formatDate, formatCurrency, relTime } from '@/lib/utils'

const SUPPORTED = new Set(SUPPORTED_TENANT_FEATURE_KEYS)
const norm = (f: string[] | null | undefined) => Array.from(new Set((f || []).map((x) => String(x).trim()).filter(Boolean)))

const TABS = [
  { id: 'general', label: 'General', icon: 'info' },
  { id: 'health', label: 'Health', icon: 'activity' },
  { id: 'usage', label: 'Uso', icon: 'trending' },
  { id: 'features', label: 'Features', icon: 'zap' },
  { id: 'stage', label: 'Stage', icon: 'package' },
  { id: 'releases', label: 'Releases', icon: 'package' },
  { id: 'provisions', label: 'Provision jobs', icon: 'cpu' },
  { id: 'billing', label: 'Billing', icon: 'card' },
]

function TenantDetailSkeleton() {
  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <Skeleton width={70} height={14} style={{ marginBottom: 10 }} />
          <div className="row gap-3" style={{ alignItems: 'center' }}>
            <Skeleton width={220} height={32} />
            <Skeleton width={84} height={22} radius={999} />
          </div>
          <Skeleton width={320} height={14} style={{ marginTop: 10 }} />
        </div>
        <div className="page-actions">
          <Skeleton width={96} height={34} radius={8} />
          <Skeleton width={84} height={34} radius={8} />
        </div>
      </div>

      <Tabs active="general" onChange={() => undefined} tabs={TABS} />

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div className="card">
          <div className="card-header"><h3>Información</h3></div>
          <div className="card-body"><Skeleton height={190} /></div>
        </div>
        <div className="card">
          <div className="card-header"><h3>Dominio personalizado</h3></div>
          <div className="card-body"><Skeleton height={120} /></div>
        </div>
      </div>
    </>
  )
}

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState('general')
  const [selectedVersion, setSelectedVersion] = useState('')
  const [selectedStageVersion, setSelectedStageVersion] = useState('')
  const [editingFeatures, setEditingFeatures] = useState<string[]>([])
  const [customDomain, setCustomDomain] = useState('')
  const [superadminOpen, setSuperadminOpen] = useState(false)
  const [superadminReason, setSuperadminReason] = useState('')

  const { data: tenant, isLoading, error } = useQuery({ queryKey: ['tenant', id], queryFn: () => getTenantById(id!), enabled: !!id })
  const { data: releasesCatalog = [] } = useQuery({ queryKey: ['releases-catalog'], queryFn: getReleasesCatalog })
  const { data: releases = [] } = useQuery({ queryKey: ['tenant-releases', id], queryFn: () => getTenantReleases(id!), enabled: !!id })
  const { data: stage, isLoading: stageLoading } = useQuery({ queryKey: ['tenant-stage', id], queryFn: () => getTenantStage(id!), enabled: !!id && tab === 'stage' })
  const { data: jobsResult, isLoading: jobsLoading } = useQuery({ queryKey: ['provision-jobs', 'tenant', id], queryFn: () => getProvisionJobs({ tenantId: id!, pageSize: 50 }), enabled: !!id && tab === 'provisions' })
  const { data: invoicesResult, isLoading: invoicesLoading } = useQuery({ queryKey: ['invoices', 'tenant', id], queryFn: () => getInvoices({ tenantId: id!, pageSize: 50 }), enabled: !!id && tab === 'billing' })

  useEffect(() => {
    setSelectedVersion(tenant?.desired_version || tenant?.current_version || tenant?.tenant_version || '')
    setCustomDomain(tenant?.custom_domain || '')
    setEditingFeatures(norm(Array.isArray(tenant?.enabled_features) ? tenant!.enabled_features! : []))
  }, [tenant?.id, tenant?.enabled_features, tenant?.desired_version, tenant?.current_version, tenant?.tenant_version, tenant?.custom_domain])

  useEffect(() => {
    const stagedRelease = releasesCatalog.find((release) => release.channel === 'staged')?.release_tag || ''
    setSelectedStageVersion(stage?.release_tag || stagedRelease)
  }, [stage?.release_tag, releasesCatalog])

  const invalidate = () => { qc.invalidateQueries({ queryKey: ['tenant', id] }); qc.invalidateQueries({ queryKey: ['tenants'] }) }

  const upgradeMutation = useMutation({
    mutationFn: async () => {
      const v = selectedVersion.trim()
      if (!v) throw new Error('Seleccioná una versión')
      const input = { version: v, components: ['system' as const] }
      try {
        return await upgradeTenant(id!, input)
      } catch (e) {
        // El catálogo no pudo ordenar las versiones: se pide confirmación explícita.
        const message = e instanceof Error ? e.message : String(e)
        if (!/force/i.test(message)) throw e
        if (!window.confirm(`${message}\n\n¿Actualizar igual? La confirmación queda registrada en el historial.`)) throw new Error('Cancelado')
        return upgradeTenant(id!, { ...input, force: true })
      }
    },
    onSuccess: () => { toast.success('Upgrade aplicado'); invalidate(); qc.invalidateQueries({ queryKey: ['tenant-releases', id] }) },
    onError: (e: any) => { if (String(e?.message || '').toLowerCase().includes('cancel')) return; toast.error(e?.message || 'No se pudo aplicar la versión') },
  })
  const rollbackMutation = useMutation({
    mutationFn: () => rollbackTenant(id!),
    onSuccess: () => { toast.success('Rollback restaurado'); invalidate(); qc.invalidateQueries({ queryKey: ['tenant-releases', id] }) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo hacer rollback'),
  })
  const featuresMutation = useMutation({
    mutationFn: () => updateTenant(id!, { enabled_features: norm(editingFeatures) }),
    onSuccess: () => { toast.success('Funcionalidades actualizadas'); invalidate() },
    onError: (e: any) => toast.error(e?.message || 'No se pudieron guardar'),
  })
  const domainMutation = useMutation({
    mutationFn: () => { const v = customDomain.trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/.*$/, ''); if (!v) throw new Error('Ingresá un dominio'); return applyTenantCustomDomain(id!, v) },
    onSuccess: () => { toast.success('Dominio aplicado'); invalidate() },
    onError: (e: any) => toast.error(e?.message || 'No se pudo aplicar el dominio'),
  })
  const provisionMutation = useMutation({
    mutationFn: () => provisionTenant(id!, { version: selectedVersion.trim() || tenant?.desired_version || tenant?.tenant_version || undefined }),
    onSuccess: () => {
      toast.success('Tenant provisionado')
      invalidate()
      qc.invalidateQueries({ queryKey: ['provision-jobs', 'tenant', id] })
      qc.invalidateQueries({ queryKey: ['tenant-releases', id] })
    },
    onError: (e: any) => toast.error(e?.message || 'No se pudo provisionar'),
  })
  const suspendMutation = useMutation({ mutationFn: () => suspendTenant(id!), onSuccess: () => { toast.success('Tenant suspendido'); invalidate() }, onError: (e: any) => toast.error(e?.message || 'Error') })
  const stageMutation = useMutation({
    mutationFn: () => { const v = selectedStageVersion.trim(); if (!v) throw new Error('Seleccioná una release staged'); return upsertTenantStage(id!, v) },
    onSuccess: () => { toast.success('Stage actualizado'); qc.invalidateQueries({ queryKey: ['tenant-stage', id] }) },
    onError: (e: any) => toast.error(e?.message || 'No se pudo actualizar stage'),
  })
  const deleteStageMutation = useMutation({
    mutationFn: () => { const c = window.prompt(`Para eliminar stage, escribí el slug exacto: ${tenant?.slug}`); if (!c) throw new Error('Cancelado'); return deleteTenantStage(id!, c) },
    onSuccess: () => { toast.success('Stage eliminado'); qc.invalidateQueries({ queryKey: ['tenant-stage', id] }) },
    onError: (e: any) => { if (String(e?.message || '').toLowerCase().includes('cancel')) return; toast.error(e?.message || 'No se pudo eliminar stage') },
  })
  const activateMutation = useMutation({ mutationFn: () => activateTenant(id!), onSuccess: () => { toast.success('Tenant activado'); invalidate() }, onError: (e: any) => toast.error(e?.message || 'Error') })
  const deleteMutation = useMutation({
    mutationFn: () => { const c = window.prompt(`Para eliminar, escribí el slug exacto: ${tenant?.slug}`); if (!c) throw new Error('Cancelado'); return deprovisionTenant(id!, c, { purge: false }) },
    onSuccess: () => { toast.success('Tienda eliminada'); qc.invalidateQueries({ queryKey: ['tenants'] }); navigate('/tenants') },
    onError: (e: any) => { if (String(e?.message || '').toLowerCase().includes('cancel')) return; toast.error(e?.message || 'No se pudo eliminar') },
  })
  const superadminMutation = useMutation({
    mutationFn: () => requestTenantSuperadminAccess(id!, superadminReason.trim()),
    onSuccess: (result) => {
      toast.success(`Solicitud enviada por WhatsApp a ${result.whatsapp_phone}`)
      setSuperadminOpen(false)
      setSuperadminReason('')
    },
    onError: (e: any) => toast.error(e?.message || 'No se pudo solicitar acceso superadmin'),
  })

  if (isLoading) return <TenantDetailSkeleton />
  if (error || !tenant) {
    return (
      <div className="empty-state"><div className="emoji"><Icon name="building" size={24} /></div><h3>No se encontró la tienda</h3><Link to="/tenants"><AppButton variant="ghost">Volver a tiendas</AppButton></Link></div>
    )
  }

  const systemVersion = tenant.current_version || tenant.tenant_version || 'n/a'
  const siteVersion = tenant.current_site_version || systemVersion
  const versionOptions = releasesCatalog.length > 0 ? releasesCatalog.map((r) => r.release_tag) : [tenant.current_version || tenant.tenant_version || ''].filter(Boolean)
  const stageVersionOptions = releasesCatalog.filter((r) => r.channel === 'staged' || String(r.release_tag || '').startsWith('staged-')).map((r) => r.release_tag)
  const toggleFeature = (key: string) => setEditingFeatures((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key])
  const jobs = jobsResult?.data || []
  const invoices = invoicesResult?.data || []

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="row gap-2" style={{ marginBottom: 6 }}>
            <Link to="/tenants" style={{ fontSize: 12.5, color: 'var(--text-3)' }}>← Tenants</Link>
          </div>
          <div className="row gap-3" style={{ alignItems: 'center' }}>
            <h1>{tenant.business_name}</h1>
            <StatusBadge status={tenant.subscription_status} size="sm" />
          </div>
          <div className="subtitle row gap-2" style={{ flexWrap: 'wrap' }}>
            <span className="code-chip">{tenant.slug}</span>
            {tenant.custom_domain ? <a href={`https://${tenant.custom_domain}`} target="_blank" rel="noreferrer">· {tenant.custom_domain}</a> : null}
          </div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="shield" onClick={() => setSuperadminOpen(true)}>Entrar como superadmin</AppButton>
          {tenant.provision_status !== 'active' ? (
            <AppButton variant="success" icon="cpu" disabled={provisionMutation.isPending} onClick={() => provisionMutation.mutate()}>
              {provisionMutation.isPending ? 'Provisionando…' : 'Provisionar'}
            </AppButton>
          ) : null}
          {tenant.subscription_status === 'suspended'
            ? <AppButton variant="secondary" icon="play" onClick={() => activateMutation.mutate()}>Reactivar</AppButton>
            : <AppButton variant="secondary" icon="pause" onClick={() => suspendMutation.mutate()}>Suspender</AppButton>}
          <AppButton variant="secondary" icon="trash2" onClick={() => deleteMutation.mutate()}>Eliminar</AppButton>
        </div>
      </div>

      <Modal
        open={superadminOpen}
        onClose={() => setSuperadminOpen(false)}
        title="Acceso superadmin"
        subtitle="Se enviará un WhatsApp a tu usuario para aprobar este acceso auditado."
        footer={(
          <>
            <AppButton variant="secondary" onClick={() => setSuperadminOpen(false)}>Cancelar</AppButton>
            <AppButton variant="primary" icon="send" disabled={superadminMutation.isPending || superadminReason.trim().length < 8} onClick={() => superadminMutation.mutate()}>Enviar WhatsApp</AppButton>
          </>
        )}
      >
        <div className="col gap-3">
          <div className="form-row">
            <label htmlFor="superadmin-reason">Razón del acceso</label>
            <textarea
              id="superadmin-reason"
              className="input"
              rows={4}
              value={superadminReason}
              onChange={(e) => setSuperadminReason(e.target.value)}
              placeholder="Ej: revisar configuración de pedidos a solicitud del cliente"
            />
          </div>
          <div className="muted" style={{ fontSize: 12.5 }}>
            El token vence rápido, es de un solo uso y el admin del tenant mostrará la etiqueta Superadmin Bisne. No se habilitan features que el tenant no tenga activas.
          </div>
        </div>
      </Modal>

      <Tabs active={tab} onChange={setTab} tabs={TABS} />

      {tab === 'general' ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div className="card">
            <div className="card-header"><h3>Información</h3></div>
            <div className="card-body">
              <dl className="dl" style={{ gridTemplateColumns: '130px 1fr' }}>
                <dt>Razón social</dt><dd>{tenant.legal_name || '—'}</dd>
                <dt>Email</dt><dd>{tenant.email || '—'}</dd>
                <dt>Teléfono</dt><dd>{tenant.phone || '—'}</dd>
                <dt>País</dt><dd>{tenant.country_name || tenant.country_code || '—'}</dd>
                <dt>Plan</dt><dd>{tenant.plan_name || '—'}</dd>
                <dt>Creado</dt><dd>{formatDate(tenant.created_at)}</dd>
              </dl>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3>Dominio personalizado</h3></div>
            <div className="card-body col gap-3">
              <div className="form-row">
                <label htmlFor="td-domain">Dominio</label>
                <input id="td-domain" className="input" value={customDomain} onChange={(e) => setCustomDomain(e.target.value)} placeholder="pedidos.minegocio.com" />
              </div>
              <AppButton variant="primary" size="sm" icon="check" disabled={domainMutation.isPending} onClick={() => domainMutation.mutate()}>Aplicar dominio</AppButton>
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'health' ? (
        <div className="card">
          <div className="card-header">
            <h3>Estado del stack</h3>
            {tenant.provision_status !== 'active' ? (
              <AppButton variant="success" size="sm" icon="cpu" disabled={provisionMutation.isPending} onClick={() => provisionMutation.mutate()}>
                {provisionMutation.isPending ? 'Provisionando…' : 'Provisionar ahora'}
              </AppButton>
            ) : null}
          </div>
          <div className="card-body">
            <dl className="dl" style={{ gridTemplateColumns: '160px 1fr' }}>
              <dt>Provisión</dt><dd><span className="row gap-2"><HealthDot status={tenant.provision_status === 'active' ? 'ok' : tenant.provision_status === 'failed' ? 'down' : 'unknown'} />{tenant.provision_status || '—'}</span></dd>
              <dt>Último release</dt><dd><StatusBadge status={tenant.last_release_status || 'unknown'} size="sm" /></dd>
              {tenant.last_release_error ? <><dt>Error</dt><dd style={{ color: 'var(--danger-fg)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{tenant.last_release_error}</dd></> : null}
              <dt>Sistema (API + admin)</dt><dd><span className="code-chip">{systemVersion}</span></dd>
              <dt>Sitio web</dt><dd><span className="code-chip">{siteVersion}</span></dd>
            </dl>
          </div>
        </div>
      ) : null}

      {tab === 'usage' ? (
        <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="kpi-card"><div className="kpi-label"><Icon name="package" size={14} />Productos</div><div className="kpi-value">{(tenant.current_products_count || 0).toLocaleString('es-AR')}</div></div>
          <div className="kpi-card"><div className="kpi-label"><Icon name="shoppingBag" size={14} />Pedidos (mes)</div><div className="kpi-value">{(tenant.current_orders_month || 0).toLocaleString('es-AR')}</div></div>
          <div className="kpi-card"><div className="kpi-label"><Icon name="hardDrive" size={14} />Storage (MB)</div><div className="kpi-value">{(tenant.current_storage_mb || 0).toLocaleString('es-AR')}</div></div>
        </div>
      ) : null}

      {tab === 'features' ? (
        <div className="card">
          <div className="card-header">
            <h3>Funcionalidades</h3>
            <AppButton variant="success" size="sm" icon="check" disabled={featuresMutation.isPending} onClick={() => featuresMutation.mutate()}>Guardar</AppButton>
          </div>
          <div className="card-body col gap-4">
            {Object.entries(FEATURE_CATEGORIES).map(([catKey, cat]) => (
              <div key={catKey}>
                <div className="t-eyebrow" style={{ marginBottom: 8 }}>{cat.label}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                  {cat.keys.filter((k) => SUPPORTED.has(k)).map((key) => {
                    const on = editingFeatures.includes(key)
                    return (
                      <label key={key} className={`feature-item ${on ? 'enabled' : ''}`} style={{ display: 'flex', gap: 8, padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', alignItems: 'center', background: on ? 'var(--success-bg)' : 'var(--surface)' }}>
                        <input type="checkbox" checked={on} onChange={() => toggleFeature(key)} />
                        <span style={{ fontSize: 12.5 }}>{key}</span>
                      </label>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {tab === 'stage' ? (
        <div className="col gap-4">
          <div className="card">
            <div className="card-header">
              <h3>Preproducción</h3>
              {stage?.url ? <a href={stage.url} target="_blank" rel="noreferrer"><AppButton variant="secondary" size="sm" icon="external">Abrir stage</AppButton></a> : null}
            </div>
            <div className="card-body">
              {stageLoading ? (
                <Skeleton height={120} />
              ) : (
                <div className="col gap-4">
                  <dl className="dl" style={{ gridTemplateColumns: '160px 1fr' }}>
                    <dt>Estado</dt><dd><StatusBadge status={stage?.status || 'not_created'} size="sm" /></dd>
                    <dt>URL</dt><dd>{stage?.url ? <a href={stage.url} target="_blank" rel="noreferrer">{stage.url}</a> : 'No creado'}</dd>
                    <dt>Runtime interno</dt><dd><span className="code-chip">{stage?.runtime_slug || `${tenant.slug}-staged`}</span></dd>
                    <dt>Release</dt><dd><span className="code-chip">{stage?.release_tag || '—'}</span></dd>
                    {stage?.last_error ? <><dt>Error</dt><dd style={{ color: 'var(--danger-fg)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>{stage.last_error}</dd></> : null}
                  </dl>

                  <div className="row gap-3" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 260, flex: 1 }}>
                      <div className="t-eyebrow">Release staged</div>
                      <select className="select" style={{ marginTop: 6 }} value={selectedStageVersion} onChange={(e) => setSelectedStageVersion(e.target.value)}>
                        <option value="">Seleccionar release</option>
                        {stageVersionOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                    <AppButton variant="success" icon="arrowUp" disabled={stageMutation.isPending || !selectedStageVersion.trim()} onClick={() => stageMutation.mutate()}>
                      {stageMutation.isPending ? 'Desplegando…' : stage ? 'Actualizar stage' : 'Crear stage'}
                    </AppButton>
                    {stage ? (
                      <AppButton variant="secondary" icon="trash2" disabled={deleteStageMutation.isPending} onClick={() => deleteStageMutation.mutate()}>
                        {deleteStageMutation.isPending ? 'Eliminando…' : 'Eliminar stage'}
                      </AppButton>
                    ) : null}
                  </div>

                  <div className="muted" style={{ fontSize: 12.5 }}>
                    Stage usa contenedores separados: <span className="code-chip">tenant-{tenant.slug}-staged-*</span>. La URL publica conserva el slug: <span className="code-chip">staged.bisne.app/{tenant.slug}</span>.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'releases' ? (
        <div className="col gap-4">
          <div className="card">
            <div className="card-header"><h3>Versión actual</h3></div>
            <div className="card-body">
              <div className="row gap-4" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="t-eyebrow">Sistema (API + admin)</div>
                  <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 24, marginTop: 2 }}>{systemVersion}</div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div className="t-eyebrow">Sitio web</div>
                  <div style={{ fontFamily: 'Bricolage Grotesque', fontWeight: 700, fontSize: 24, marginTop: 2 }}>{siteVersion}</div>
                </div>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div className="t-eyebrow">Versión deseada</div>
                  <select className="select" style={{ marginTop: 6 }} value={selectedVersion} onChange={(e) => setSelectedVersion(e.target.value)}>
                    {versionOptions.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </div>
                <div className="row gap-2">
                  <AppButton variant="secondary" icon="rotateCcw" disabled={rollbackMutation.isPending} onClick={() => rollbackMutation.mutate()}>{rollbackMutation.isPending ? 'Volviendo…' : 'Rollback'}</AppButton>
                  <AppButton variant="success" icon="arrowUp" disabled={upgradeMutation.isPending} onClick={() => upgradeMutation.mutate()}>{upgradeMutation.isPending ? 'Actualizando…' : 'Upgrade'}</AppButton>
                </div>
              </div>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 8 }}>
                El upgrade actualiza el Sistema (API + admin). El sitio web se administra en su propio repositorio.
              </div>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3>Historial de releases</h3></div>
            <div className="card-body flush">
              {releases.length === 0 ? (
                <EmptyState icon="package" title="Sin releases" desc="Este tenant todavía no tiene historial." />
              ) : (
                <table className="table">
                  <thead><tr><th>Acción</th><th>De → A</th><th>Status</th><th>Cuándo</th></tr></thead>
                  <tbody>
                    {releases.map((r) => (
                      <tr key={r.id}>
                        <td><span className="badge badge-outline badge-sm">{r.action}</span></td>
                        <td>
                          <span className="code-chip">{r.from_version || '—'}</span> → <span className="code-chip">{r.to_version || '—'}</span>
                          {r.from_site_version || r.to_site_version ? (
                            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>
                              Sitio web: <span className="code-chip">{r.from_site_version || r.from_version || '—'}</span> → <span className="code-chip">{r.to_site_version || r.to_version || '—'}</span>
                            </div>
                          ) : null}
                        </td>
                        <td><StatusBadge status={r.status} size="sm" /></td>
                        <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(r.created_at)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {tab === 'provisions' ? (
        <div className="card">
          <div className="card-header">
            <h3>Provision jobs</h3>
            {tenant.provision_status !== 'active' ? (
              <AppButton variant="success" size="sm" icon="cpu" disabled={provisionMutation.isPending} onClick={() => provisionMutation.mutate()}>
                {provisionMutation.isPending ? 'Provisionando…' : 'Provisionar'}
              </AppButton>
            ) : null}
          </div>
          <div className="card-body flush">
            {jobsLoading ? (
              <TableSkeleton columns={4} rows={6} />
            ) : jobs.length === 0 ? (
              <EmptyState icon="cpu" title="Sin jobs" desc="No hay jobs de provisión para este tenant." />
            ) : (
              <table className="table">
                <thead><tr><th>Acción</th><th>Status</th><th style={{ textAlign: 'right' }}>Duración</th><th>Cuándo</th></tr></thead>
                <tbody>
                  {jobs.map((j) => (
                    <tr key={j.id}>
                      <td><span className="badge badge-outline badge-sm">{j.action}</span></td>
                      <td><StatusBadge status={j.status} size="sm" /></td>
                      <td style={{ textAlign: 'right' }}><span className="tabular" style={{ fontSize: 12 }}>{j.duration_ms ? (j.duration_ms / 1000).toFixed(1) + 's' : '—'}</span></td>
                      <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{relTime(j.created_at)}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      {tab === 'billing' ? (
        <div className="col gap-4">
          <div className="card">
            <div className="card-header"><h3>Plan</h3></div>
            <div className="card-body">
              <dl className="dl" style={{ gridTemplateColumns: '130px 1fr' }}>
                <dt>Plan</dt><dd>{tenant.plan_name || '—'}</dd>
                <dt>Estado</dt><dd><StatusBadge status={tenant.subscription_status} size="sm" /></dd>
                <dt>Trial hasta</dt><dd>{tenant.trial_ends_at ? formatDate(tenant.trial_ends_at) : '—'}</dd>
              </dl>
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3>Facturas</h3></div>
            <div className="card-body flush">
              {invoicesLoading ? (
                <TableSkeleton columns={4} rows={6} />
              ) : invoices.length === 0 ? (
                <EmptyState icon="file" title="Sin facturas" desc="Este tenant no tiene facturas." />
              ) : (
                <table className="table">
                  <thead><tr><th>Número</th><th style={{ textAlign: 'right' }}>Total</th><th>Status</th><th>Emitida</th></tr></thead>
                  <tbody>
                    {invoices.map((inv) => (
                      <tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)} {...rowActivation(() => navigate(`/invoices/${inv.id}`))}>
                        <td><span className="code-chip">{inv.number || inv.id.slice(0, 8)}</span></td>
                        <td style={{ textAlign: 'right' }}><span className="tabular">{formatCurrency(inv.total || 0, inv.currency || 'ARS')}</span></td>
                        <td><StatusBadge status={inv.status} size="sm" /></td>
                        <td><span style={{ fontSize: 12 }}>{inv.issued_at ? formatDate(inv.issued_at) : '—'}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}
